import { useEffect, useState } from 'react'
import ZoomControls from '@/components/ZoomControls/ZoomControls'
import useAppContext from '@/hooks/useAppContext'
import { useEditorContext, usePixelManipulationEditorContext } from '@/scenes/engine'
import { useSelector } from 'react-redux'
import { selectZoomRatio, selectDisableEditorState } from '@/store/slices/editor/selector'

function Zoom() {
  const { isOpenPixelManipulationObject, toolType } = useAppContext()

  const mainEditor = useEditorContext().editor
  const mainZoomRatio = useSelector(selectZoomRatio)
  const disableEditorState = useSelector(selectDisableEditorState)

  const { editor: pixelEditor, canvas } = usePixelManipulationEditorContext()
  const pixelHandler = pixelEditor?.handlers?.pixelManipulationObjectHandler

  const [pixelZoomRatio, setPixelZoomRatio] = useState(1)

  const [openResetZoom, setOpenResetZoom] = useState(false)


  /**
   * Handle pixel manipulation zoom effects (only when pixel manipulation overlay is active)
   */
  useEffect(() => {
    if (!isOpenPixelManipulationObject) {
      setPixelZoomRatio(1)
      return
    }
    if (!canvas || !pixelEditor) return
    setOpenResetZoom(pixelHandler?.syncWithMainCanvas ? !mainEditor.handlers.zoomHandler.isFit() : pixelZoomRatio !== 1)
    if (!pixelHandler.isZoomAvailable()) return
    // Sync brush size to zoom
    pixelEditor.handlers.pixelManipulationZoomHandler.currentZoom = pixelZoomRatio
    pixelEditor.handlers.pixelManipulationCanvasHandler.setBrushSizeOnZoom()
  }, [pixelZoomRatio, isOpenPixelManipulationObject, canvas, pixelEditor, pixelHandler, mainZoomRatio])

  /**
   * Listen to internal pixel zoom ratio events
   */
  useEffect(() => {
    if (!isOpenPixelManipulationObject) return
    if (!pixelEditor) return
    const handleUpdateZoomRatio = (zoomRatio: number) => {
      setPixelZoomRatio(zoomRatio)
    }
    pixelEditor.on('updateZoomRatio', handleUpdateZoomRatio)
    return () => {
      pixelEditor?.off('updateZoomRatio', handleUpdateZoomRatio)
    }
  }, [pixelEditor, isOpenPixelManipulationObject])

  /**
   * When in main editor mode (no pixel manipulation), compute reset button visibility
   */
  useEffect(() => {
    if (isOpenPixelManipulationObject) return
    if (!mainEditor) return
    
    // Use a small delay to ensure fitZoomRatio is updated after mainZoomRatio
    const timeoutId = setTimeout(() => {
      const isFit = mainEditor.handlers.zoomHandler.isFit()
      setOpenResetZoom(!isFit)
    }, 0)
    
    return () => clearTimeout(timeoutId)
  }, [isOpenPixelManipulationObject, mainEditor, mainZoomRatio])


  // Determine values & handlers based on mode
  const isSync = isOpenPixelManipulationObject ? pixelHandler?.syncWithMainCanvas : true
  const displayZoom = isOpenPixelManipulationObject
    ? isSync
      ? Math.min(mainZoomRatio * 100, 300)
      : pixelZoomRatio // slider value
    : Math.min(mainZoomRatio * 100, 300)

  const handleReset = () => {
    if (isOpenPixelManipulationObject) {
      if (toolType === 'Eraser' && pixelHandler?.syncWithMainCanvas) {
        mainEditor?.handlers.zoomHandler.zoomToFit()
        mainEditor?.handlers.scrollbarHandler.hideScrollbar()
        pixelHandler?.updatePositionFollowMainCanvas()
      } else {
        pixelEditor?.handlers.pixelManipulationZoomHandler.zoomToFit()
      }
      setOpenResetZoom(false)
      return
    }
    // Main editor mode
    mainEditor?.handlers.zoomHandler.zoomToFit()
    mainEditor?.handlers.scrollbarHandler.hideScrollbar()
    setOpenResetZoom(false)
  }

  const handleZoomOut = () => {
    if (isOpenPixelManipulationObject) {
      if (isSync) {
        mainZoomRatio >= 0.1 && mainEditor?.handlers.zoomHandler.zoomOut()
        pixelHandler?.updatePositionFollowMainCanvas()
      } else {
        if (!pixelEditor) return
        const zoomStep = pixelEditor.handlers.pixelManipulationZoomHandler.baseStep
        if (Number((pixelZoomRatio - zoomStep).toFixed(1)) < 1) return
        setPixelZoomRatio(pre => Number((pre - zoomStep).toFixed(1)))
        pixelEditor.handlers.pixelManipulationZoomHandler.zoomOut()
      }
      return
    }
    // Main mode
    mainZoomRatio >= 0.1 && mainEditor?.handlers.zoomHandler.zoomOut()
  }

  const handleZoomIn = () => {
    if (isOpenPixelManipulationObject) {
      if (isSync) {
        mainZoomRatio <= 30 && mainEditor?.handlers.zoomHandler.zoomIn()
        pixelHandler?.updatePositionFollowMainCanvas()
      } else {
        if (!pixelEditor) return
        const zoomStep = pixelEditor.handlers.pixelManipulationZoomHandler.baseStep
        if (pixelZoomRatio + zoomStep > 5) return
        setPixelZoomRatio(pre => pre + zoomStep)
        pixelEditor.handlers.pixelManipulationZoomHandler.zoomIn()
      }
      return
    }
    mainZoomRatio <= 30 && mainEditor?.handlers.zoomHandler.zoomIn()
  }

  const handleSliderChange = (value: number) => {
    if (isOpenPixelManipulationObject) {
      if (isSync) {
        const ratio = value / 100
        mainEditor?.handlers.zoomHandler.zoomToRatio(ratio)
        pixelHandler?.updatePositionFollowMainCanvas()
      } else {
        if (!pixelEditor) return
        setPixelZoomRatio(value)
        pixelEditor.handlers.pixelManipulationZoomHandler.prevZoom =
          pixelEditor.handlers.pixelManipulationZoomHandler.currentZoom
        pixelEditor.handlers.pixelManipulationZoomHandler.currentZoom = value
        pixelEditor.handlers.pixelManipulationZoomHandler.updateZoom()
      }
      return
    }
    // Main mode
    const ratio = value / 100
    mainEditor?.handlers.zoomHandler.zoomToRatio(ratio)
  }

  // Slider range / step depending on mode
  const min = isOpenPixelManipulationObject
    ? pixelEditor?.handlers.pixelManipulationZoomHandler.minZoom || 1
    : 10
  const max = isOpenPixelManipulationObject
    ? pixelEditor?.handlers.pixelManipulationZoomHandler.maxZoom || 5
    : 300
  const step = isOpenPixelManipulationObject
    ? pixelEditor?.handlers.pixelManipulationZoomHandler.sliderStep || 0.1
    : 2.9

  const isZoomDisabled = disableEditorState.zoom === 'disabled'
  const trackPercent = isOpenPixelManipulationObject
    ? isSync
      ? ((mainZoomRatio - 0.1) / 2.9) * 100
      : ((pixelZoomRatio - 1) / 4) * 100
    : ((mainZoomRatio - 0.1) / 2.9) * 100

  return (
    <ZoomControls
      value={displayZoom}
      min={min}
      max={max}
      step={step}
      disabled={isZoomDisabled}
      showReset={openResetZoom}
      trackPercent={trackPercent}
      onReset={handleReset}
      onZoomOut={handleZoomOut}
      onZoomIn={handleZoomIn}
      onChange={handleSliderChange}
    />
  )
}

export default Zoom
